// Knowledge base (document folders), after SFolders. Home: search over file names, summaries and full text, scope
// chips, and the folders as cards. A folder: Edit folder and Add files, its description, a drop zone of its own
// (uploading, then Reading..., then the summary; a name already there asks "add as version N, or keep both"), then
// the files. Questions about the folder go through the shell's Ask AI button, scoped to the folder (Kareem, 5 Nov:
// no Ask this folder box on the page).
import { AddButton } from '../ui/AddButton';
import { useAiScope } from '../assistant/AiButton';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { can, type FolderDetail, type SessionUser, shownSpaceColor } from '@dpw/shared';
import { useSpaces } from '../lib/queries';
import { usePhone } from '../lib/media';
import { listItem } from '../lib/motion';
import { ErrorNote } from '../ui/bits';
import { Skel, SkelList } from '../ui/motion';
import { useToast } from '../ui/toast';
import { IconChevL, IconSearch, IconUpload, IconWarn, IconX } from '../ui/icons';
import { useFile, useFolder, useFolders, useSearch, useUploads } from '../kb/hooks';
import { dayText, lengthOf, lineOf, scopeDot } from '../kb/model';
import { FolderCard, Scope, TypeTag, filesText, useSpaceColors } from '../kb/parts';
import { FileSheet } from '../kb/FileSheet';
import { FolderForm } from '../kb/FolderForm';
import { PhoneFolder, PhoneFolders } from '../phone/Folders';

export function KnowledgeHome({ me }: { me: SessionUser }) {
  const phone = usePhone();
  const [params, setParams] = useSearchParams();
  const newIn = params.get('new');
  const closeNew = () => setParams({}, { replace: true });
  if (phone) return <PhoneFolders me={me} newIn={newIn} onCloseNew={closeNew} />;
  return <DesktopHome me={me} newIn={newIn} onCloseNew={closeNew} />;
}

function DesktopHome({ me, newIn, onCloseNew }: { me: SessionUser; newIn: string | null; onCloseNew: () => void }) {
  const folders = useFolders();
  const spaces = useSpaces().data ?? [];
  const colors = useSpaceColors();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [scope, setScope] = useState<string>('all');
  const [newOpen, setNewOpen] = useState(false);
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 220); return () => clearTimeout(t); }, [q]);
  const hits = useSearch(dq);
  const all = folders.data ?? [];
  const inScope = all.filter(f => scope === 'all' || (scope === 'team' ? !f.spaceId : f.spaceId === scope));
  const shown = inScope.filter(f => !dq || `${f.name} ${f.description}`.toLowerCase().includes(dq.toLowerCase()));
  const scopes = [['all', 'All', ''], ['team', 'Team-wide', scopeDot(null)], ...spaces.map(s => [s.id, s.name, scopeDot(shownSpaceColor(s.color))])] as [string, string, string][];
  const hitRows = (hits.data ?? []).filter(h => scope === 'all' || h.file.alsoIn.some(l => l.kind === 'folder' && (scope === 'team' ? !l.spaceId : l.spaceId === scope)));
  const openHit = (h: (typeof hitRows)[number]) => {
    const fo = h.file.alsoIn.find(l => l.kind === 'folder');
    const page = h.matched === 'text' && h.ref ? `&page=${h.ref}` : '';
    nav(fo ? `/folders/${fo.id}?file=${h.file.id}${page}` : `/files/${h.file.id}`);
  };
  return (
    <div className="kb col" style={{ gap: 18 }}>
      <div className="hdr"><div className="htl"><h1>Knowledge base</h1><span className="xs mut">Documents the team and the assistant work from</span></div>
        <div className="hact">{can(me.role, 'file.write') && <AddButton onClick={() => setNewOpen(true)}>New folder</AddButton>}</div></div>
      <div className="kbtool">
        <label className="srch"><IconSearch /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search files and summaries" aria-label="Search files and summaries" autoComplete="off" />
          {q && <button className="ib xq" onClick={() => setQ('')} aria-label="Clear search"><IconX /></button>}</label>
        <div className="fchips" role="group" aria-label="Where">{scopes.map(([k, l, dot]) => (
          <button key={k} className={`fc${scope === k ? ' on' : ''}`} aria-pressed={scope === k} onClick={() => setScope(k)}>{dot && <i style={{ background: dot }} />}{l}<span>{all.filter(f => k === 'all' || (k === 'team' ? !f.spaceId : f.spaceId === k)).length}</span></button>
        ))}</div>
      </div>
      <div className="xs mut kbnote">The assistant reads folder descriptions first, then file summaries, then opens only the files it needs.</div>
      {dq.length >= 2 && <>
        <div className="panel" role="region" aria-label="Files found">
          <div className="sh" style={{ marginBottom: 4 }}><b>Files</b><span>{hits.isLoading ? 'Searching' : `${hitRows.length} ${hitRows.length === 1 ? 'file matches' : 'files match'} by name, summary or full text`}</span></div>
          {hitRows.map(h => {
            const fo = h.file.alsoIn.find(l => l.kind === 'folder');
            const where = h.matched === 'name' ? 'name' : h.matched === 'summary' ? `summary${h.ref ? `, p. ${h.ref}` : ''}` : `full text${h.ref ? `, ${h.file.unit === 'sheet' ? 'sheet' : h.file.unit === 'slide' ? 'slide' : 'p.'} ${h.ref}` : ''}`;
            return <button key={h.file.id} className="hit" onClick={() => openHit(h)}><TypeTag name={h.file.name} /><span className="fnt"><span className="fn1"><b>{h.file.name}</b></span><span className="hw">{fo?.name ?? h.file.activityTitle ?? 'Files'} · found in the {where}</span></span></button>;
          })}
          {!hits.isLoading && !hitRows.length && <div className="sm mut" style={{ padding: '8px 0' }}>No file names, summaries or text match. Try fewer words.</div>}
        </div>
        <div className="sh"><b>Folders</b><span>{shown.length} {shown.length === 1 ? 'folder matches' : 'folders match'}</span></div>
      </>}
      {folders.isLoading ? <div className="fgrid">{[0, 1, 2, 3].map(i => <div key={i} className="fcard"><Skel w="40%" h={14} /><Skel w="70%" h={18} /><Skel h={12} /><Skel w="80%" h={12} /></div>)}</div>
        : folders.error ? <ErrorNote error={folders.error} /> : (
          <div className="fgrid">{shown.map(f => <FolderCard key={f.id} f={f} colors={colors} />)}</div>
        )}
      {!folders.isLoading && !shown.length && <div className="panel sm mut">{all.length ? 'No folders match. Clear the search or pick another place.' : 'No folders yet.'}</div>}
      <AnimatePresence>{(newOpen || newIn !== null) && <FolderForm key="new" spaceId={newIn && newIn !== 'team' ? newIn : null} onClose={() => { setNewOpen(false); onCloseNew(); }} />}</AnimatePresence>
    </div>
  );
}

export function FolderPage({ me }: { me: SessionUser }) {
  const { id = '' } = useParams();
  const phone = usePhone();
  const folder = useFolder(id);
  // The AI button looks at this folder, or at the file open in it (?file=).
  const [qp] = useSearchParams();
  const fileOpen = qp.get('file');
  const fileName = folder.data?.files.find(f => f.id === fileOpen)?.name ?? null;
  useAiScope(folder.data ? (fileOpen ? { fileId: fileOpen, folderId: folder.data.id, spaceId: folder.data.spaceId, label: fileName ?? folder.data.name } : { folderId: folder.data.id, spaceId: folder.data.spaceId, label: folder.data.name }) : null);
  if (folder.isLoading) return <div className="col gap12" role="status" aria-label="Loading"><Skel w="30%" h={14} /><Skel w="50%" h={26} /><Skel h={90} /><SkelList rows={6} variant="conv" /></div>;
  if (folder.error || !folder.data) return <ErrorNote error={folder.error} />;
  return phone ? <PhoneFolder me={me} fo={folder.data} /> : <DesktopFolder me={me} fo={folder.data} />;
}

function DesktopFolder({ me, fo }: { me: SessionUser; fo: FolderDetail }) {
  const colors = useSpaceColors();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const fileId = params.get('file');
  const page = params.get('page') ? Number(params.get('page')) : null;
  const kp = params.get('kp') ? Number(params.get('kp')) : null;
  const openFile = (fid: string, extra: Record<string, string> = {}) => setParams({ file: fid, ...extra });
  const closeFile = () => setParams({}, { replace: false });
  const [sheet, setSheet] = useState<'none' | 'edit'>('none');
  const [all, setAll] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const writer = fo.canAdd;
  const up = useUploads(fo.id, {
    onDone: (f, how) => toast(how === 'version' ? `Added as version ${f.version}. The assistant compares it with the last one.` : `Added ${f.name}. The assistant is reading it.`),
    onError: e => toast(e instanceof Error ? e.message : 'The upload did not work.'),
  });
  const SHOW = 12;
  const files = all ? fo.files : fo.files.slice(0, SHOW);
  // A file that was being read is now ready: say so.
  const reading = useRef(new Set<string>());
  useEffect(() => {
    for (const f of fo.files) {
      if (f.status === 'reading') reading.current.add(f.id);
      else if (reading.current.delete(f.id) && f.status === 'ready' && !fileId) toast(`Summary ready: ${f.name}`);
    }
  }, [fo.files]);

  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); if (writer && e.dataTransfer.files.length) up.add(e.dataTransfer.files); };
  const pickFiles = () => input.current?.click();
  const n = fo.files.length;

  return (
    <div className="kb col" style={{ gap: 18 }} onDragOver={e => { if (writer) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)} onDrop={drop}>
      <Link className="backl" to="/folders"><IconChevL />Knowledge base</Link>
      <div className="hdr" style={{ marginTop: -8 }}>
        <div className="htl"><h1>{fo.name}</h1><Scope f={fo} colors={colors} /><span className="xs mut">{filesText(n)} · updated {dayText(fo.lastChange.at) === 'Today' ? 'today' : dayText(fo.lastChange.at)}{fo.lastChange.by ? ` by ${fo.lastChange.by.split(' ')[0]}` : ''}</span></div>
        <div className="hact">
          {fo.canEdit && <button className="bt" onClick={() => setSheet('edit')}>Edit folder</button>}
          {writer && <AddButton onClick={pickFiles}>Add files</AddButton>}
        </div>
      </div>
      {fo.description && <p className="fdesc">{fo.description}</p>}

      {writer && <section className="addf" aria-label="Add files">
        <AnimatePresence initial={false}>{up.conflict && (
          <motion.div key={up.conflict.existing.id + up.conflict.nextVersion} className="cfl" role="alert" variants={listItem} initial="initial" animate="animate" exit="exit"><IconWarn />
            <div className="t"><b>{up.conflict.existing.name} is already in this folder</b><span className="mut">Add it as version {up.conflict.nextVersion} to keep one file with its history, or keep both as separate files.</span></div>
            <button className="bt pri" onClick={() => up.resolve('version')}>Add as version {up.conflict.nextVersion}</button><button className="bt" onClick={() => up.resolve('keep_both')}>Keep both</button><button className="bt ghost" onClick={() => up.resolve('cancel')}>Cancel</button>
          </motion.div>
        )}</AnimatePresence>
        <button className={`dropz${over ? ' over' : ''}`} onClick={pickFiles}><IconUpload /><span>Drop files here or <u>choose files</u>. PDF, Word, Excel, PowerPoint, images, text and CSV, up to 50 MB each.</span></button>
      </section>}

      <div className="panel fpan" role="region" aria-label="Files">
        <div className="fbar"><b>Files</b><span className="xs mut">{filesText(n)} · one copy each, linked wherever it is used</span></div>
        <input ref={input} type="file" multiple hidden accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv" onChange={e => { if (e.target.files?.length) up.add(e.target.files); e.target.value = ''; }} data-testid="kb-file-input" />
        <div className="fth" aria-hidden="true"><span>Name and summary</span><span>Length</span><span>Added by</span><span>Updated</span><span>Version</span></div>
        <div>
          {up.pending.filter(p => !p.fileId).map(p => (
            <div className="frow fresh" key={p.key} data-file={p.name}><span className="fnm"><TypeTag name={p.name} /><span className="fnt"><span className="fn1"><b>{p.name}</b><span className="nwf">New</span></span>
              <span className="fl1 up1"><span className="pb"><i style={{ width: `${p.pct}%`, background: 'var(--ink)' }} /></span>Uploading {p.pct}%</span></span></span>
              <span className="fc2" /><span className="fc2">{me.name.split(' ')[0]}</span><span className="fc2 mono">Just now</span><span className="fc2 mono">v1</span></div>
          ))}
          {files.map(f => {
            const line = lineOf(f);
            const fresh = up.fresh.includes(f.id);
            const sending = up.pending.find(p => p.fileId === f.id);
            return (
              <button key={f.id} className={`frow${fresh ? ' fresh' : ''}`} onClick={() => openFile(f.id)} data-file={f.name}>
                <span className="fnm"><TypeTag name={f.name} /><span className="fnt">
                  <span className="fn1"><b>{f.name}</b>{f.ocr && <span className="ocr">Read with OCR</span>}{fresh && <span className="nwf">New</span>}</span>
                  {sending && <span className="fl1 up1"><span className="pb"><i style={{ width: `${sending.pct}%`, background: 'var(--ink)' }} /></span>Uploading version {f.version + 1}, {sending.pct}%</span>}
                  {!sending && line.kind === 'ok' && <span className="fl1">{line.text}</span>}
                  {!sending && line.kind === 'reading' && <span className="fl1 rd1"><span className="aiorb" />{line.text}</span>}
                  {!sending && line.kind === 'locked' && <span className="fl1 lk1">{line.text}</span>}
                </span></span>
                <span className="fc2">{lengthOf(f)}</span><span className="fc2">{(f.versionBy ?? f.addedBy)?.name.split(' ')[0] ?? ''}</span><span className="fc2 mono">{dayText(f.updatedAt)}</span><span className="fc2 mono">v{f.version}</span>
              </button>
            );
          })}
        </div>
        {!n && !up.pending.length && <div className="sm mut" style={{ padding: '6px 4px 14px' }}>No files yet.{writer ? ` Drop them in Add files above, or attach them in the Assistant and say "put this in ${fo.name}".` : ''}</div>}
        {!all && n > SHOW && <div style={{ padding: '4px 0 10px' }}><button className="bt" onClick={() => setAll(true)}>Show all {n} files</button></div>}
      </div>
      <AnimatePresence>
        {fileId && <FileSheet key={fileId} fileId={fileId} folder={fo} me={me} page={page} kp={kp} onClose={closeFile} />}
        {sheet === 'edit' && <FolderForm key="edit" edit={fo} onClose={() => setSheet('none')} />}
      </AnimatePresence>
    </div>
  );
}

/** /files/:id (assistant sources, search): the file in its first folder, at the cited page; else on its own. */
export function FileRoute({ me }: { me: SessionUser }) {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const f = useFile(id);
  const nav = useNavigate();
  if (f.isLoading) return <div role="status" aria-label="Loading"><Skel w="40%" h={22} /></div>;
  if (f.error || !f.data) return <ErrorNote error={f.error} />;
  const fo = f.data.alsoIn.find(l => l.kind === 'folder');
  const ref = params.get('page') ?? params.get('sheet') ?? params.get('slide') ?? params.get('section');
  if (fo) return <Navigate replace to={`/folders/${fo.id}?file=${id}${ref ? `&page=${ref}` : ''}`} />;
  return <div className="kb"><AnimatePresence><FileSheet key={id} fileId={id} folder={null} me={me} page={ref ? Number(ref) : null} onClose={() => nav(-1)} /></AnimatePresence></div>;
}
